import React, { useRef, useEffect, useContext } from 'react';
import Dialog from '@ferris/react/components/dialog/Dialog';
import PropTypes from 'prop-types';
import { CardContent } from '@ferris/react/components/cards';
import { useMutation, gql } from '@apollo/client';
import { TrackingContext } from '@vrbo/react-event-tracking';
import delay from 'lodash.delay';

import LeadForm from '../LeadForm';
import Vacasa from './Vacasa';
import SubmissionForm from '../SubmissionForm';
import { createInteractionEvent } from '../../../utils/EventProviders/EgClickstreamUtil';

export const SUBMIT_VACASA = gql`
mutation postLeadFormToBucket($data: PropertyManagmentInput!) {
    postLeadFormToBucket(data: $data)
}
`;

function VacasaComponent(props) {
    const {
        closeLabel,
        submitLabel,
        title,
        description,
        open,
        setShowVacasa,
        isVacasaViewed,
        isFrboFlow,
        isModalOpen,
        setVacasaViewed,
        firstNameLabel,
        firstName,
        firstNameValidation,
        lastNameLabel,
        setFirstName,
        lastName,
        lastNameValidation,
        setLastName,
        emailLabel,
        email,
        setEmail,
        leadFormTitle,
        leadFormDescription,
        address,
        countryList,
        addressValidation,
        getTranslation,
        brand,
        locale,
        setAddressField,
        leadFormSubmit,
        leadFormPolicy,
        phoneValidation,
        emailValidation,
        validateEmail,
        emailValidationErrorText,
        successTitle,
        successDescription,
        successCta,
        failureTitle,
        failureDescription,
        failureCta,
        vacasaStep,
        setVacasaStep,
        vacasaVariant,
        isModal,
        phoneNumber,
        phoneCountryCodeDigits
    } = props;

    const isVariantA = vacasaVariant === 1;
    const currentCountry = useRef(address.country);
    const { trigger } = useContext(TrackingContext);
    const [postLeadFormToBucket, { loading }] = useMutation(SUBMIT_VACASA);

    const leadFn = isModal ? () => {
        setVacasaStep(2);
        trigger('interaction', createInteractionEvent('referral_popup.selected', 'Property Onboarding', '', 'referral_popup'));
    } : () => {
        setVacasaStep(2);
        setShowVacasa(true);
        trigger('interaction', createInteractionEvent('referral_popup.selected', 'Property Onboarding', '', 'referral_section'));
    };

    if (!isModal) {
        return (
            <Vacasa
                title={title}
                description={description}
                submitLabel={submitLabel}
                leadFn={leadFn}
                closeLabel={closeLabel}
                isModal={isModal}
                setShowVacasa={setShowVacasa}
                open={open}
                vacasaVariant={vacasaVariant}
            />
        );
    }

    useEffect(() => {
        currentCountry.current = address.country;
    }, [address.country]);

    const shouldShowVacasa = useRef(!isVacasaViewed && isFrboFlow);
    const getIsVacasaViewed = () => {
        const country = currentCountry.current;
        const isCountryAllowed = !country || countryList.some((c) => c.value === country);
        if (
            isCountryAllowed
            && shouldShowVacasa.current
        ) {
            setShowVacasa(true);
            setVacasaViewed(true);
        }
    };

    const onSubmit = async () => {
        trigger('interaction', createInteractionEvent('referral_popup.submitted', 'Property Onboarding', '', isVariantA ? 'referral_popup' : 'referral_section'));
        const {
            address1, address2, city, stateProvince, postalCode, country
        } = address;
        const data = {
            firstName,
            lastName,
            email,
            phoneNumber: `${phoneCountryCodeDigits}${phoneNumber}`,
            address1,
            address2,
            city,
            stateProvince,
            postalCode,
            country
        };
        try {
            await postLeadFormToBucket({
                variables: {
                    data
                }
            });
            setVacasaStep(3);
        } catch (e) {
            setVacasaStep(4);
        }
    };

    const renderStep = () => {
        switch (vacasaStep) {
        case 1:
            return (
                <Vacasa
                    title={title}
                    description={description}
                    submitLabel={submitLabel}
                    leadFn={leadFn}
                    closeLabel={closeLabel}
                    isModal={isModal}
                    setShowVacasa={setShowVacasa}
                    open={open}
                    vacasaVariant={vacasaVariant}
                />
            );
        case 2:
            return (
                <LeadForm
                    leadFormTitle={leadFormTitle}
                    leadFormDescription={leadFormDescription}
                    firstNameLabel={firstNameLabel}
                    firstName={firstName}
                    firstNameValidation={firstNameValidation}
                    setFirstName={setFirstName}
                    lastNameLabel={lastNameLabel}
                    lastName={lastName}
                    lastNameValidation={lastNameValidation}
                    setLastName={setLastName}
                    emailLabel={emailLabel}
                    email={email}
                    setEmail={setEmail}
                    address={address}
                    countryList={countryList}
                    addressValidation={addressValidation}
                    getTranslation={getTranslation}
                    brand={brand}
                    locale={locale}
                    setAddressField={setAddressField}
                    closeLabel={closeLabel}
                    setShowVacasa={setShowVacasa}
                    leadFormSubmit={leadFormSubmit}
                    leadFormPolicy={leadFormPolicy}
                    phoneValidation={phoneValidation}
                    emailValidation={emailValidation}
                    emailValidationErrorText={emailValidationErrorText}
                    validateEmail={validateEmail}
                    onSubmit={onSubmit}
                    isLoading={loading}
                />
            );
        case 3:
            return (
                <SubmissionForm
                    title={successTitle}
                    description={successDescription}
                    ctaLabel={successCta}
                    setShowVacasa={setShowVacasa}
                />
            );
        case 4:
            return (
                <SubmissionForm
                    title={failureTitle}
                    description={failureDescription}
                    ctaLabel={failureCta}
                    setShowVacasa={setShowVacasa}
                />
            );
        default:
            return null;
        }
    };

    useEffect(() => {
        if (isVariantA) {
            if (isVacasaViewed) {
                shouldShowVacasa.current = false;
            } else {
                shouldShowVacasa.current = !isModalOpen && isFrboFlow;
            }
        }
    }, [isVacasaViewed, isModalOpen]);

    useEffect(() => {
        if (isVariantA) {
            delay(() => {
                document.documentElement.addEventListener('mouseleave', getIsVacasaViewed);
            }, 10000);
        }
        return () => {
            if (isVariantA) {
                document.documentElement.removeEventListener('mouseleave', getIsVacasaViewed);
            }
        };
    }, []);

    return (
        <Dialog
            open={open}
            fullScreen
            showFooter={false}
            showHeader={false}
            collapsePadding={vacasaStep === 1}
            classes="ulx-property-managment"
        >
            <CardContent
                collapsePadding={vacasaStep === 1}
                separatorBottom={false}
                separatorTop={false}
            >
                {renderStep()}
            </CardContent>
        </Dialog>
    );
}

const ValidationObject = {
    isValid: PropTypes.bool.isRequired,
    error: PropTypes.string
};

VacasaComponent.propTypes = {
    closeLabel: PropTypes.string.isRequired,
    submitLabel: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    open: PropTypes.bool.isRequired,
    setShowVacasa: PropTypes.func.isRequired,
    isVacasaViewed: PropTypes.bool.isRequired,
    isFrboFlow: PropTypes.bool.isRequired,
    isModalOpen: PropTypes.bool.isRequired,
    setVacasaViewed: PropTypes.func.isRequired,
    leadFormTitle: PropTypes.string.isRequired,
    leadFormDescription: PropTypes.string.isRequired,
    firstNameLabel: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    firstNameValidation: PropTypes.shape(ValidationObject).isRequired,
    setFirstName: PropTypes.func.isRequired,
    lastNameLabel: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    lastNameValidation: PropTypes.shape(ValidationObject).isRequired,
    setLastName: PropTypes.func.isRequired,
    emailLabel: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    setEmail: PropTypes.func.isRequired,
    address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
    }).isRequired,
    countryList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
    })).isRequired,
    addressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        })
    }).isRequired,
    getTranslation: PropTypes.func.isRequired,
    brand: PropTypes.string.isRequired,
    locale: PropTypes.shape({
        canonical: PropTypes.string,
        display: PropTypes.string
    }).isRequired,
    setAddressField: PropTypes.func.isRequired,
    leadFormSubmit: PropTypes.string.isRequired,
    leadFormPolicy: PropTypes.string.isRequired,
    phoneValidation: PropTypes.shape(ValidationObject).isRequired,
    emailValidation: PropTypes.shape(ValidationObject).isRequired,
    validateEmail: PropTypes.func.isRequired,
    emailValidationErrorText: PropTypes.string.isRequired,
    successTitle: PropTypes.string.isRequired,
    successDescription: PropTypes.string.isRequired,
    successCta: PropTypes.string.isRequired,
    failureTitle: PropTypes.string.isRequired,
    failureDescription: PropTypes.string.isRequired,
    failureCta: PropTypes.string.isRequired,
    vacasaStep: PropTypes.number.isRequired,
    setVacasaStep: PropTypes.func.isRequired,
    phoneCountryCodeDigits: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    isModal: PropTypes.bool.isRequired,
    vacasaVariant: PropTypes.number.isRequired
};

export default VacasaComponent;
